import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Bug, LogOut, Shield } from 'lucide-react';
import useChat from '../../hooks/useChat';
import Sidebar from '../Layout/Sidebar';
import MessageList from './MessageList';
import InputArea from './InputArea';
import DebugPanel from './DebugPanel';
import SuggestedSearches from './SuggestedSearches';
import { QCDashboard } from '../QC';
import AccountSelector from '../Shared/AccountSelector';
import WorkspaceSelector from '../Shared/WorkspaceSelector';
import AgentSelector from '../Shared/AgentSelector';
import SessionList from './SessionList';
import AgentDetailsDialog from './AgentDetailsDialog';
import { getAgentProfile, getAgentAvatarUrl } from '../../api/client';
import AgentWorkbenchIcon from './AgentWorkbenchIcon';
import ArtstashDamIcon from './ArtstashDamIcon';
import {
  buildContextPath,
  buildAccountPath,
  getRequestedContext,
  getRequestedWorkspaceId,
  RequestedContext,
} from '../../utils/requestedContext';
import { buildDamUrl, buildQueryContextUrl } from '../../utils/appSwitcherUrls';
import { useAnalyticsIdentity } from '../../services/analytics';

interface ChatContainerProps {
  onLogout?: () => void;
  userEmail?: string;
  userName?: string;
  userId?: string;
  authToken?: string | null;
  onPrivacySettings?: () => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ onLogout, userEmail, userName, userId, authToken, onPrivacySettings }) => {
  const [requestedContext, setRequestedContext] = useState(getRequestedContext);
  const [inputValue, setInputValue] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState<string | null>(null);
  const [selectedAgentSlug, setSelectedAgentSlug] = useState<string | null>(null);
  const [selectedAgentName, setSelectedAgentName] = useState<string | null>(null);
  const [selectedAgentHasProfile, setSelectedAgentHasProfile] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);
  const [agentProfile, setAgentProfile] = useState<{ agent_name?: string; tagline?: string; description?: string } | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  // sessionId uses state only for explicit session selection (sidebar click).
  // Auto-assigned IDs from session_init are stored in a ref to avoid reconnects.
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const activeSessionId = useRef<string | null>(null);
  const [chatNonce, setChatNonce] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [showQC, setShowQC] = useState(false);
  const pendingAccountNavigation = useRef(false);
  const isInternal = userEmail?.endsWith('@artstash.io') || userEmail?.endsWith('@artstash.local');

  useAnalyticsIdentity({ userId, accountId: selectedAccountId });

  const userInitials = useMemo(() => {
    if (userName) {
      const parts = userName.trim().split(/\s+/);
      return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
    }
    if (userEmail) {
      const local = userEmail.split('@')[0];
      const parts = local.split(/[._-]/);
      return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : local.slice(0, 2).toUpperCase();
    }
    return '?';
  }, [userName, userEmail]);

  // Close menus when clicking outside
  useEffect(() => {
    if (!showUserMenu && !showAppSwitcher) return;
    const handleClick = () => { setShowUserMenu(false); setShowAppSwitcher(false); };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showUserMenu, showAppSwitcher]);

  useEffect(() => {
    const handlePopState = () => {
      pendingAccountNavigation.current = false;
      setRequestedContext(getRequestedContext());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8005';
  const wsBaseUrl = apiUrl.replace(/^http/, 'ws') + '/ws/chat';

  let wsUrl = '';
  if (selectedAccountId && selectedWorkspaceId && selectedAgentSlug) {
      const params = new URLSearchParams();
      params.append('accountId', selectedAccountId);
      if (selectedAccountName) params.append('accountName', selectedAccountName);
      if (selectedWorkspaceId) params.append('workspaceId', selectedWorkspaceId);
      if (selectedWorkspaceName) params.append('workspaceName', selectedWorkspaceName);
      if (selectedAgentSlug) params.append('agentSlug', selectedAgentSlug);
      if (selectedSessionId) params.append('sessionId', selectedSessionId);
      if (chatNonce) params.append('_n', String(chatNonce));
      wsUrl = `${wsBaseUrl}?${params.toString()}`;
  }

  const handleSessionIdReceived = useCallback((id: string) => {
      // Store in ref (no reconnect) — only use state for explicit session selection
      activeSessionId.current = id;
  }, []);

  // Reset downstream selections when parent changes
  useEffect(() => {
      setSelectedWorkspaceId(null);
      setSelectedWorkspaceName(null);
      setSelectedAgentSlug(null);
      setSelectedAgentName(null);
      setSelectedAgentHasProfile(false);
      setAgentProfile(null);
      setAgentAvatarUrl(null);
      setSelectedSessionId(null);
  }, [selectedAccountId]);

  useEffect(() => {
      setSelectedAgentSlug(null);
      setSelectedAgentName(null);
      setSelectedAgentHasProfile(false);
      setAgentAvatarUrl(null);
      setAgentProfile(null);
      setSelectedSessionId(null);
  }, [selectedWorkspaceId]);

  useEffect(() => {
      setSelectedSessionId(null);
  }, [selectedAgentSlug]);

  // Fetch agent profile and avatar when agent with profile is selected
  useEffect(() => {
      if (!selectedAgentSlug || !selectedAccountId || !selectedWorkspaceId || !selectedAgentHasProfile) {
          setAgentProfile(null);
          setAgentAvatarUrl(null);
          return;
      }
      let cancelled = false;
      const fetchProfile = async () => {
          try {
              const [profile, avatarUrl] = await Promise.all([
                  getAgentProfile(selectedAgentSlug, selectedAccountId, selectedWorkspaceId, selectedWorkspaceName || undefined),
                  getAgentAvatarUrl(selectedAgentSlug, selectedAccountId, selectedWorkspaceId, selectedWorkspaceName || undefined),
              ]);
              if (!cancelled) {
                  setAgentProfile(profile);
                  setAgentAvatarUrl(avatarUrl);
              }
          } catch (err) {
              console.error('Failed to fetch agent profile:', err);
          }
      };
      fetchProfile();
      return () => { cancelled = true; };
  }, [selectedAgentSlug, selectedAccountId, selectedWorkspaceId, selectedWorkspaceName, selectedAgentHasProfile]);

  const handleNewChat = () => {
      setSelectedSessionId(null);
      activeSessionId.current = null;
      setChatNonce(n => n + 1);
  };

  const chatAnalyticsContext = useMemo(() => ({
      accountId: selectedAccountId,
      workspaceId: selectedWorkspaceId,
      agentSlug: selectedAgentSlug,
      selectedSessionId,
  }), [selectedAccountId, selectedWorkspaceId, selectedAgentSlug, selectedSessionId]);

  const { messages, sendMessage, error, isConnected, isResponding, debugEvents, sendRaw, welcomeMessage, suggestedSearches, thinkingStatus } = useChat(
    wsUrl,
    handleSessionIdReceived,
    chatAnalyticsContext
  );

  const handleSend = () => {
    if (!inputValue.trim() || isResponding) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleSuggestionSelect = (query: string) => {
    if (isResponding) return;
    sendMessage(query);
  };

  const handleQC = (assetId: string) => {
    sendMessage(`QC asset ${assetId}`);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void handleQC;

  const updateAddress = (
    accountId: string,
    workspaceId: string,
    method: 'push' | 'replace'
  ) => {
    const nextContext: RequestedContext = { accountId, workspaceId };
    const nextPath = buildContextPath(accountId, workspaceId);
    setRequestedContext(nextContext);
    if (`${window.location.pathname}${window.location.search}` === nextPath) return;
    window.history[method === 'push' ? 'pushState' : 'replaceState']({}, '', nextPath);
  };

  const handleAccountSelect = (id: string, name: string, source: 'resolve' | 'user') => {
    if (source === 'user') {
      pendingAccountNavigation.current = true;
      setRequestedContext({ accountId: id, workspaceId: null });
      window.history.pushState({}, '', buildAccountPath(id));
    }
    setSelectedAccountId(id);
    setSelectedAccountName(name);
  };

  const handleWorkspaceSelect = (id: string, name: string, source: 'resolve' | 'user') => {
    setSelectedWorkspaceId(id);
    setSelectedWorkspaceName(name);
    if (selectedAccountId) {
      const shouldReplace = pendingAccountNavigation.current;
      pendingAccountNavigation.current = false;
      updateAddress(selectedAccountId, id, source === 'user' && !shouldReplace ? 'push' : 'replace');
    }
  };

  const handleAgentSelect = (slug: string, name: string, hasProfile: boolean) => {
    setSelectedAgentSlug(slug);
    setSelectedAgentName(name);
    setSelectedAgentHasProfile(hasProfile);
  };

  return (
    <div className="app-root" style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      {/* ─── App Header (Artstash-style, full width) ─── */}
      <header className="app-header">
        <div className="app-header-left">
          <img src="/logo.svg" alt="Artstash" className="app-header-logo" />
        </div>
        <div className="app-header-right">
          {isInternal && (
          <>
          <button
            onClick={() => { setShowQC(!showQC); if (!showQC) setShowDebug(false); }}
            title="QC Engine"
            className="app-header-btn"
            style={{ color: showQC ? '#f59e0b' : '#ffffff' }}
          >
            <Shield size={24} />
          </button>
          <button
            onClick={() => { setShowDebug(!showDebug); if (!showDebug) setShowQC(false); }}
            title="Toggle Debug View"
            className="app-header-btn"
            style={{ color: showDebug ? '#8b5cf6' : '#ffffff' }}
          >
            <Bug size={24} />
          </button>
          </>
          )}
          <a
            href="https://artstash.io/support"
            target="_blank"
            rel="noopener noreferrer"
            title="Help & Support"
            className="app-header-btn"
            style={{ color: '#ffffff' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 18h2v-2h-2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4" />
            </svg>
          </a>
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAppSwitcher(!showAppSwitcher); setShowUserMenu(false); }}
              title="App Switcher"
              className="app-header-btn"
              style={{ color: '#ffffff', padding: 0 }}
            >
                <svg width="24" height="24" fill="white" focusable="false" aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M4 8h4V4H4zm6 12h4v-4h-4zm-6 0h4v-4H4zm0-6h4v-4H4zm6 0h4v-4h-4zm6-10v4h4V4zm-6 4h4V4h-4zm6 6h4v-4h-4zm0 6h4v-4h-4z"></path>
                </svg>
            </button>
            {showAppSwitcher && (() => {
              const isDev = window.location.hostname.includes('-dev');
              const suffix = isDev ? '-dev' : '';
              const contextAccountId = selectedAccountId || requestedContext.accountId;
              const contextWorkspaceId = contextAccountId === selectedAccountId
                ? selectedWorkspaceId
                : requestedContext.workspaceId;
              const damUrl = buildDamUrl(`https://app${suffix}.artstash.io`, contextAccountId, contextWorkspaceId);
              const workbenchUrl = buildQueryContextUrl(`https://agents${suffix}.artstash.io`, contextAccountId, contextWorkspaceId);
              return (
              <div className="header-app-switcher-menu">
                <a href={damUrl} target="_blank" rel="noopener noreferrer" className="header-app-switcher-item">
                  <span className="header-app-switcher-icon">
                    <ArtstashDamIcon />
                  </span>
                  Artstash DAM
                </a>
                <a href={workbenchUrl} target="_blank" rel="noopener noreferrer" className="header-app-switcher-item">
                  <span className="header-app-switcher-icon">
                    <AgentWorkbenchIcon />
                  </span>
                  Agent Workbench
                </a>
              </div>
              );
            })()}
          </div>
          {onLogout && (
            <div style={{ position: 'relative' }}>
              <button type="button" aria-label="User menu" aria-expanded={showUserMenu}
                className="header-user-avatar"
                onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); setShowAppSwitcher(false); }}
                title={userName || userEmail || 'User'}
              >
                {userInitials}
              </button>
              {showUserMenu && (
                <div className="header-user-menu">
                  {(userName || userEmail) && (
                    <div className="header-user-menu-info">
                      {userName && <span className="header-user-menu-name">{userName}</span>}
                      {userEmail && <span className="header-user-menu-email">{userEmail}</span>}
                    </div>
                  )}
                  {onPrivacySettings && <button className="header-user-menu-item"
                    onClick={() => { setShowUserMenu(false); onPrivacySettings(); }}>Privacy settings</button>}
                  <button onClick={() => { setShowUserMenu(false); onLogout(); }} className="header-user-menu-item">
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ─── Body: Sidebar + Chat ─── */}
      <div className="main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar
          navigation={
            <nav className="context-navigation" aria-label="Account and workspace navigation">
              <AccountSelector
                onSelect={handleAccountSelect}
                selectedAccountId={selectedAccountId}
                requestedAccountId={requestedContext.accountId}
              />
              <WorkspaceSelector
                accountId={selectedAccountId}
                onSelect={handleWorkspaceSelect}
                selectedWorkspaceId={selectedWorkspaceId}
                requestedWorkspaceId={getRequestedWorkspaceId(requestedContext, selectedAccountId)}
              />
            </nav>
          }
        >
            <SessionList
               accountId={selectedAccountId}
               selectedSessionId={selectedSessionId}
               onSelectSession={setSelectedSessionId}
               onNewChat={handleNewChat}
            />
        </Sidebar>
        <div className="chat-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#121212', position: 'relative' }}>
          <div className="chat-context-header">
            <div className="chat-context-title">
              <span>{selectedAccountName || 'Account'}</span>
              <h1>{selectedWorkspaceName || 'Select a workspace'}</h1>
            </div>
            <div className="chat-agent-controls">
              <AgentSelector
                accountId={selectedAccountId}
                workspaceId={selectedWorkspaceId}
                workspaceName={selectedWorkspaceName}
                onSelect={handleAgentSelect}
                selectedSlug={selectedAgentSlug}
              />
              <AgentDetailsDialog
                accountId={selectedAccountId}
                workspaceId={selectedWorkspaceId}
                workspaceName={selectedWorkspaceName}
                agentSlug={selectedAgentSlug}
                agentName={selectedAgentName}
              />
            </div>
          </div>

          {error && (
            <div style={{ background: 'rgba(220, 38, 38, 0.15)', color: '#ef4444', padding: '0.5rem', textAlign: 'center', fontSize: '0.875rem', borderBottom: '1px solid rgba(220, 38, 38, 0.3)' }}>
              {error} - {isConnected ? 'Connected' : 'Disconnected'}
            </div>
          )}

          {showDebug && <DebugPanel events={debugEvents} onClose={() => setShowDebug(false)} accountId={selectedAccountId || undefined} workspaceId={selectedWorkspaceId || undefined} agentSlug={selectedAgentSlug || undefined} sendRaw={sendRaw} messages={messages} />}
          {showQC ? (
            <div style={{ flex: 1, overflow: 'auto' }}>
              <QCDashboard authToken={authToken} />
            </div>
          ) : (
          <>
          {messages.length === 0 && suggestedSearches.length > 0 ? (
            <SuggestedSearches
              welcomeMessage={welcomeMessage}
              suggestions={suggestedSearches}
              onSelect={handleSuggestionSelect}
              agentAvatarUrl={agentAvatarUrl}
              agentProfile={agentProfile}
            />
          ) : (
            <MessageList messages={messages} thinkingStatus={thinkingStatus} />
          )}
          <InputArea
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            disabled={!isConnected || isResponding}
          />
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;
